import { transcribeChunk } from 'api/noteTakerSpeechApi'
import {
	TRANSCRIPT_SEGMENT_STATUS,
	createTranscriptSegment,
} from '../types/transcriptSegment'
import { createTranscriptChunkQueue } from '../services/transcriptChunkQueue'

/**
 * Live transcript via independent audio windows.
 *
 * Each audio window from the recorder is a self-contained WebM file, transcribed
 * once and appended as its own final segment. Window lengths follow a warm-up
 * schedule (5s × 3, then 10s). Previously transcribed audio is never re-sent.
 */
export function createNoteTakerSttTranscriptProvider({ store }) {
	let recordingId = ''
	let queue = null
	let lastWindowEndMs = 0

	const ensureQueue = () => {
		if (queue) return queue

		queue = createTranscriptChunkQueue({
			maxConcurrency: 1,
			transcribe: async (job, signal) => {
				store.beginTranscriptStt?.()
				try {
					return await transcribeChunk({
						recordingId,
						chunkId: job.chunkId,
						blob: job.blob,
						mimeType: job.mimeType,
						startMs: job.startMs,
						endMs: job.endMs,
						language: 'en-US',
						signal,
					})
				} finally {
					store.endTranscriptStt?.()
				}
			},
			onJobSuccess: (job, result) => {
				const text = String(result.text || '').trim()

				if (text) {
					store.upsertTranscriptSegment(
						createTranscriptSegment({
							chunkId: job.chunkId,
							text,
							startMs: job.startMs,
							endMs: result.endMs ?? job.endMs,
							speaker: result.speaker ?? null,
							status: TRANSCRIPT_SEGMENT_STATUS.FINAL,
						}),
					)
				}

				store.setTranscriptError(null)
			},
			onJobError: (job, error) => {
				store.setTranscriptError({
					code: error?.name || 'TranscriptionError',
					message: error?.message || 'Transcription failed',
					chunkId: job.chunkId,
				})
			},
		})

		return queue
	}

	const handleWindow = ({ blob, mimeType, windowIndex, timestampMs }) => {
		if (!recordingId || !blob || blob.size === 0) return

		const chunkId = windowIndex + 1
		const startMs = lastWindowEndMs
		const endMs = Number(timestampMs) || startMs
		lastWindowEndMs = endMs

		ensureQueue().enqueue({
			chunkId,
			blob,
			mimeType: mimeType || blob.type || 'audio/webm',
			startMs,
			endMs,
		})
	}

	return {
		startRecording(nextRecordingId) {
			recordingId = String(nextRecordingId || '')
			lastWindowEndMs = 0
			queue?.dispose()
			queue = null
			ensureQueue().reset()
		},

		// STT is driven by complete windows, not raw timeslice chunks.
		handleChunk() {},

		handleWindow,

		handleStop() {},

		async flush(options) {
			if (!queue) return
			await queue.flush(options)
		},

		dispose() {
			queue?.dispose()
			queue = null
			recordingId = ''
			lastWindowEndMs = 0
		},
	}
}
